/**
 * Förstaparts-observability-funnel för /ny-hemsida — REN DIAGNOSTIK.
 * -----------------------------------------------------------------
 * Skapar ett slumpmässigt, KORTLIVAT, PSEUDONYMT visit_id per faktisk
 * /ny-hemsida-landning. Lever ENDAST i JS-minne — ingen cookie, ingen
 * localStorage/sessionStorage, ingen IP, user-agent, gclid/fbclid eller PII.
 * Refresh = nytt besök (nytt visit_id). Ett visit_id är ALDRIG en unik person.
 *
 * Den här modulen ändrar INTE consent-gating, Meta/GA4-regler, attribution,
 * bannerdesign eller formulärflöde. Den loggar bara passivt vad som hände.
 *
 * Ordning är deterministisk: landing-requesten skapas ALLTID först. Alla
 * efterföljande diagnostikevent (consent-knapp, meta_pageview, ga4_config)
 * chainas bakom landing-promiset så att de aldrig tappas pga race.
 */
import { API_URL } from "@/utils/api";
import { getConsent, hasDecision } from "@/utils/cookieConsent";

let _visitId = null;
let _landingReady = null; // Promise som resolvar när landing-POST avslutats.

function post(body) {
  try {
    return fetch(`${API_URL}/api/analytics/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* diagnostik får aldrig krascha sidan */
    return Promise.resolve();
  }
}

// Chaina ett diagnostikevent bakom landing så att landing garanterat skapats
// först (upsert sker endast på landing — övriga event uppdaterar dokumentet).
function afterLanding(body) {
  if (!_visitId) return;
  const run = () => post(body);
  if (_landingReady && typeof _landingReady.then === "function") {
    _landingReady.then(run, run);
  } else {
    run();
  }
}

function utm() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || null,
      utm_campaign: p.get("utm_campaign") || null,
      utm_content: p.get("utm_content") || null,
    };
  } catch {
    return {};
  }
}

// Redan lagrat samtycke VID ANKOMST — separat från dagens knappklick.
// Sparar BÅDE statistics och marketing som arrival-state när beslut finns.
function arrivalState() {
  try {
    if (!hasDecision()) {
      return { stored_consent: "no_prior_decision", stored_statistics: null, stored_marketing: null };
    }
    const c = getConsent();
    return {
      stored_consent: c.marketing ? "stored_granted" : "stored_denied",
      stored_statistics: !!c.statistics,
      stored_marketing: !!c.marketing,
    };
  } catch {
    return { stored_consent: "no_prior_decision", stored_statistics: null, stored_marketing: null };
  }
}

/** Startar ett besök endast vid en faktisk /ny-hemsida-landning. */
export function maybeStartVisit() {
  if (typeof window === "undefined") return;
  if (window.location.pathname !== "/ny-hemsida") return;
  if (_visitId) return;
  _visitId = "v_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
  _landingReady = post({
    visit_id: _visitId,
    event: "landing",
    path: "/ny-hemsida",
    ...arrivalState(),
    ...utm(),
  });
}

export function getVisitId() {
  return _visitId;
}

/** Exakt knapp som användes (accept_all | reject_all | custom_save). */
export function logConsentButton(button, consent) {
  afterLanding({
    visit_id: _visitId,
    event: button,
    statistics: !!(consent && consent.statistics),
    marketing: !!(consent && consent.marketing),
  });
}

/** Passiv flagga: Meta Pixel PageView faktiskt anropad för detta besök. */
export function logMetaPageview() {
  afterLanding({ visit_id: _visitId, event: "meta_pageview_called" });
}

/** Passiv flagga: GA4 config faktiskt anropad för detta besök. */
export function logGa4Config() {
  afterLanding({ visit_id: _visitId, event: "ga4_config_called" });
}
